/**
 * @file Component registry.
 *
 * Components are PURE DATA. They never contain behaviour: all behaviour lives in
 * systems (see {@link ../ecs/System.js}). A component type is identified at
 * runtime by a unique bit, which lets an entity express "which components do I
 * own" as a single integer mask and lets the world answer queries with one
 * bitwise AND instead of a map lookup per component.
 */

/** Maximum number of distinct component types (one bit each in a 31-bit mask). */
export const MAX_COMPONENT_TYPES = 31;

let nextBitIndex = 0;

/** @type {Map<Function, number>} */
const registeredTypes = new Map();

/**
 * Base class for every component. Extending it is optional but keeps intent
 * explicit and gives every component a stable `reset()` contract for pooling.
 */
export class Component {
  /**
   * Restores the component to a freshly-constructed state.
   *
   * The world currently pools entities and meshes but not components, so
   * nothing calls this yet. It exists as the agreed contract for the day
   * component pooling is added: implement `reset()` and the pool works, with no
   * change to any system.
   *
   * @returns {void}
   */
  reset() {}
}

/**
 * Registers a component type and assigns it a unique bit.
 * Registering the same type twice is a no-op and returns the existing bit.
 *
 * @param {Function} Type Component constructor.
 * @returns {number} The bit assigned to this component type.
 */
export function registerComponent(Type) {
  const existing = registeredTypes.get(Type);
  if (existing !== undefined) return existing;

  if (nextBitIndex >= MAX_COMPONENT_TYPES) {
    throw new RangeError(
      `Component type limit reached (${MAX_COMPONENT_TYPES}). ` +
        'Widen the mask to a BigInt or a Uint32Array before adding more types.'
    );
  }

  const bit = 1 << nextBitIndex++;
  registeredTypes.set(Type, bit);
  Type.bit = bit;
  Type.componentName = Type.name;
  return bit;
}

/**
 * Registers several component types at once, in declaration order.
 * @param {...Function} Types Component constructors.
 * @returns {void}
 */
export function registerComponents(...Types) {
  for (const Type of Types) registerComponent(Type);
}

/**
 * Returns the bit of an already-registered component type.
 * @param {Function} Type Component constructor.
 * @returns {number}
 */
export function bitOf(Type) {
  const bit = registeredTypes.get(Type);
  if (bit === undefined) {
    throw new ReferenceError(`Component "${Type?.name ?? Type}" was never registered.`);
  }
  return bit;
}

/**
 * Builds a query mask out of component types.
 * @param {...Function} Types Component constructors.
 * @returns {number} Combined bitmask.
 */
export function maskOf(...Types) {
  let mask = 0;
  for (const Type of Types) mask |= bitOf(Type);
  return mask;
}
